/**
 * Level selector component for exam simulation.
 *
 * Displays CEFR levels (A1-C2) for selection. All levels are unlocked and
 * selectable for exam simulation.
 *
 * @module LevelSelector
 */

import { CheckCircle, Lock } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CEFRLevel } from '@/types/exam-simulation';
import { useTranslation } from 'react-i18next';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { useAuth } from '@/hooks/useAuth';

interface LevelOption {
  code: CEFRLevel;
  isUnlocked: boolean;
}

interface LevelSelectorProps {
  readonly onSelect: (level: CEFRLevel) => void;
  readonly selectedLevel?: CEFRLevel | null;
  readonly examType?: string | null;
}

const levels: LevelOption[] = [
  { code: 'A1', isUnlocked: true },
  { code: 'A2', isUnlocked: false },
  { code: 'B1', isUnlocked: false },
  { code: 'B2', isUnlocked: false },
  { code: 'C1', isUnlocked: false },
  { code: 'C2', isUnlocked: false },
];

const getExamTypeName = (examType: string | null | undefined): string => {
  switch (examType) {
    case 'goethe':
      return 'Goethe';
    case 'osd':
      return 'ÖSD';
    case 'telc':
      return 'telc';
    default:
      return '';
  }
};

export function LevelSelector({
  onSelect,
  selectedLevel,
  examType,
}: LevelSelectorProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const examTypeName = getExamTypeName(examType);
  const isAdmin = user?.userType === 'admin';

  // Level-locked plans (Free/Starter/Exam-Ready with a chosen level) may only
  // start their locked level. Until a level is locked, all content-unlocked
  // levels remain selectable.
  const { plan } = useCurrentPlan(!isAdmin);
  const lockedLevel =
    !isAdmin && plan && plan.limits.level_scope === 'single_locked'
      ? plan.chosen_level
      : null;

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
          {t('levelSelection.title', {
            defaultValue: 'Select Proficiency Level',
          })}
        </h2>
        {examTypeName && (
          <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">
            {t('levelSelection.subtitle', {
              defaultValue:
                'Choose your CEFR proficiency level for {{examTypeName}}',
              examTypeName,
            })}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {levels.map((level) => {
          const isSelected = selectedLevel === level.code;
          const isPlanLocked =
            lockedLevel !== null && level.code !== lockedLevel;
          const isContentUnlocked =
            level.isUnlocked || (isAdmin && level.code === 'B1');
          const isUnlocked = isContentUnlocked && !isPlanLocked;

          const levelName = t(`levelSelection.levels.${level.code}.name`);
          const ariaLabel = isSelected
            ? `${level.code} – ${levelName}, ${t('levelSelection.selected', { defaultValue: 'Selected' })}`
            : isUnlocked
              ? `${level.code} – ${levelName}`
              : `${level.code} – ${levelName}, ${t('common.comingSoon', { defaultValue: 'Coming Soon' })}`;

          return (
            <Card
              key={level.code}
              role="button"
              tabIndex={0}
              aria-disabled={!isUnlocked}
              aria-label={ariaLabel}
              className={cn(
                'relative transition-all duration-200 overflow-hidden',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                isUnlocked
                  ? 'cursor-pointer hover:shadow-lg hover:border-primary/40'
                  : 'opacity-60 grayscale cursor-not-allowed border-muted',
                isSelected
                  ? 'border-primary ring-1 ring-primary/20 shadow-md bg-primary/[0.02]'
                  : 'bg-card',
              )}
              onClick={() => isUnlocked && onSelect(level.code)}
              onKeyDown={(e) => {
                if (isUnlocked && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(level.code);
                }
              }}
            >
              {isPlanLocked && (
                <div className="absolute top-2 right-2">
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-500 border border-amber-500/20">
                    <Lock className="h-3 w-3" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      {t('levelSelection.planLocked', {
                        defaultValue: 'Plan level',
                      })}
                    </span>
                  </div>
                </div>
              )}
              {!isPlanLocked && !isUnlocked && (
                <div className="absolute top-2 right-2">
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-500 border border-amber-500/20">
                    <Lock className="h-3 w-3" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      {t('common.comingSoon', { defaultValue: 'Coming Soon' })}
                    </span>
                  </div>
                </div>
              )}
              <CardHeader className="pb-3 pt-6 px-5 flex flex-row items-center sm:flex-col sm:text-center gap-4 sm:gap-0">
                <div className="flex-shrink-0 sm:mb-4">
                  <div
                    className={cn(
                      'w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-xl sm:text-2xl font-bold transition-transform duration-200',
                      isSelected &&
                        'bg-primary text-primary-foreground scale-110',
                      !isSelected &&
                        isUnlocked &&
                        'bg-muted/50 text-foreground',
                      !isSelected &&
                        !isUnlocked &&
                        'bg-muted text-muted-foreground',
                    )}
                  >
                    {isSelected ? (
                      <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8" />
                    ) : (
                      level.code
                    )}
                  </div>
                </div>

                <div className="flex-1 sm:text-center min-w-0">
                  <CardTitle className="text-lg sm:text-xl font-bold truncate">
                    {level.code} -{' '}
                    {t(`levelSelection.levels.${level.code}.name`)}
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm line-clamp-1 sm:line-clamp-2 mt-0.5">
                    {t(`levelSelection.levels.${level.code}.description`)}
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="px-5 pb-6 space-y-4">
                <div className="space-y-2 hidden sm:block">
                  <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground/80">
                    {t('levelSelection.keySkills', {
                      defaultValue: 'Key Skills',
                    })}
                  </h4>
                  <div className="space-y-1.5">
                    {(
                      t(`levelSelection.levels.${level.code}.skills`, {
                        returnObjects: true,
                      }) as string[]
                    )
                      .slice(0, 3)
                      .map((skill) => (
                        <div
                          key={`${level.code}-${skill}`}
                          className="text-xs text-muted-foreground flex items-center bg-muted/30 p-1.5 rounded-md border border-transparent hover:border-border/50 transition-colors"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/40 mr-2 shrink-0"></div>
                          <span className="truncate">{skill}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {isSelected && (
                  <div className="flex justify-center sm:pt-2">
                    <Badge className="bg-primary hover:bg-primary px-3 py-1">
                      <CheckCircle className="h-3 w-3 mr-1.5" />
                      {t('levelSelection.selected', {
                        defaultValue: 'Selected',
                      })}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
